import { useEffect, useRef, useSyncExternalStore } from "react";
import { refresh } from "./api.ts";
import type { Session, SessionEvent } from "./types.ts";

export type LiveMessage = { kind: "event"; event: SessionEvent } | { kind: "session"; session: Session } | { kind: "hello" };

type Listener = (msg: LiveMessage) => void;

/**
 * One WebSocket to /ws for the whole app, reconnecting with backoff. Every
 * message also revalidates the cached API views it affects, so lists and the
 * overview stay current without polling.
 */
class LiveSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<() => void>();
  private retry = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private pending = new Set<string>();
  connected = false;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.open();
  }

  stop(): void {
    this.started = false;
    clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
  }

  private open(): void {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setConnected(true);
      // Anything could have changed while we were away.
      refresh("/api/");
    };
    ws.onmessage = (e) => {
      let msg: LiveMessage;
      try {
        msg = JSON.parse(String(e.data));
      } catch {
        return;
      }
      this.invalidate(msg);
      for (const l of this.listeners) l(msg);
    };
    ws.onclose = () => {
      this.setConnected(false);
      if (!this.started) return;
      const delay = Math.min(15_000, 500 * 2 ** this.retry++);
      this.timer = setTimeout(() => this.open(), delay);
    };
  }

  private setConnected(v: boolean) {
    this.connected = v;
    for (const l of this.statusListeners) l();
  }

  /** Coalesces revalidation so a burst of tool events causes one refetch per view. */
  private invalidate(msg: LiveMessage): void {
    if (msg.kind === "hello") return;
    const sid = msg.kind === "event" ? msg.event.sessionId : msg.session.id;
    this.pending.add("/api/overview");
    this.pending.add("/api/sessions");
    this.pending.add("/api/projects/");
    if (msg.kind === "event") {
      const t = msg.event.type;
      if (t === "question" || t === "permission" || t === "prompt_resolved") this.pending.add("/api/prompts");
      if (t !== "tool_use" && t !== "tool_result" && t !== "assistant_text") this.pending.add("/api/activity");
      if (t === "result") this.pending.add(`/api/sessions/${sid}/diff`);
    }
    if (this.refreshTimer) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = undefined;
      const prefixes = [...this.pending];
      this.pending.clear();
      // Don't refetch a session's event log: the session page appends live events itself.
      refresh(...prefixes.filter((p) => !p.endsWith("/events")));
    }, 400);
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  subscribeStatus = (l: () => void): (() => void) => {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  };
}

export const live = new LiveSocket();

export function useLive(handler: Listener): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => live.subscribe((m) => ref.current(m)), []);
}

export function useLiveConnected(): boolean {
  return useSyncExternalStore(live.subscribeStatus, () => live.connected);
}
